import React from "react";
import { Backdrop, Box, CircularProgress, Grid } from "@mui/material";
import { DeleteOutline } from "@mui/icons-material";
import { Action, Column } from "@material-table/core";
import { IdStudent } from "src/app/services/openApi";
import Table from "src/app/components/Table";
import { useToken } from "src/app/hooks";
import {
  useEditStudentPost,
  useGroupsNamesGet,
  useStudentsListGet,
  useStudentsListPost,
} from "src/app/services/apiHooks";
import ContentLoading from "src/app/components/ContentLoading";
import GroupSelect from "src/features/settings/components/SettingsView/StudentsSettings/GroupSelect";
import { getGroupName } from "src/features/settings/components/SettingsView/StudentsSettings/utils";
import { isValidPhoneNumber } from "src/app/utils";
import AddStudentDialog from "src/features/settings/components/SettingsView/StudentsSettings/AddStudentDialog";
import UploadStudentList from "src/features/settings/components/SettingsView/StudentsSettings/UploadStudentList";
import { isNil } from "lodash";

const StudentsSettings: React.FC = () => {
  const token = useToken();
  const { data: students } = useStudentsListGet({
    token,
  });
  const { data: groupsNames } = useGroupsNamesGet({
    token,
  });
  const [editStudent, { isLoading: isEditing }] = useEditStudentPost();
  const [setStudentsList, { isLoading: isSavingList }] = useStudentsListPost();
  const isMutating = isEditing || isSavingList;

  const columns: Column<IdStudent>[] = [
    {
      title: "שם התלמיד",
      field: "name",
      type: "string",
      width: "30%",
    },
    {
      title: "מספר טלפון",
      field: "phone_num",
      type: "string",
      width: "20%",
    },
    {
      title: "קבוצה",
      field: "group",
      type: "string",
      width: "40%",
      render: (rowData) => getGroupName(rowData.group, groupsNames ?? []),
      editComponent: (props) => (
        <GroupSelect
          group={isNil(props.value) ? -1 : props.value}
          onChange={props.onChange}
        />
      ),
    },
  ];

  const handleRowUpdate = async (student: IdStudent): Promise<any> => {
    // Validate at save-time only (not as a column `validate`), so that
    // incomplete records can still be deleted from the table.
    if (!student.name) {
      window.alert("שדה השם הוא חובה");
      return Promise.reject();
    }
    if (student.phone_num && !isValidPhoneNumber(student.phone_num)) {
      window.alert("מספר טלפון חייב להכיל מספרים בלבד");
      return Promise.reject();
    }
    editStudent({
      token,
      id: student.ID,
      name: student.name,
      phoneNum: student.phone_num,
      group: student.group === -1 ? undefined : student.group,
    });
  };

  const handleRowDelete = async (student: IdStudent): Promise<any> => {
    if (students)
      setStudentsList({
        token,
        body: students.filter((s) => s.ID !== student.ID),
      });
  };

  const handleBulkDelete = (selected: IdStudent[]) => {
    if (!students) return;
    const selectedIds = new Set(selected.map((s) => s.ID));
    setStudentsList({
      token,
      body: students.filter((s) => !selectedIds.has(s.ID)),
    });
  };

  const actions: Action<IdStudent>[] = [
    {
      tooltip: "מחיקת התלמידים שנבחרו",
      icon: () => <DeleteOutline />,
      position: "toolbarOnSelect",
      onClick: (_event, data) =>
        handleBulkDelete(Array.isArray(data) ? data : [data]),
    },
  ];

  if (!students) return <ContentLoading />;

  return (
    <Grid item xs={12}>
      <Table
        title="תלמידים"
        columns={columns}
        data={students}
        onRowUpdate={handleRowUpdate}
        onRowDelete={handleRowDelete}
        selection
        actions={actions}
        selectionLabel={(count) =>
          count === 1 ? "תלמיד אחד נבחר" : `${count} תלמידים נבחרו`
        }
        pageSize={20}
        toolbarComponents={{
          left: (
            <Box gap={1} display="flex">
              <UploadStudentList />
              <AddStudentDialog />
            </Box>
          ),
        }}
        toolbarActions={{
          isWithExport: true,
        }}
      />
      <Backdrop
        open={isMutating}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.modal + 1,
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Grid>
  );
};

export default StudentsSettings;
